/**
 * React Hook for Vault & Loadout Management (Phase 55)
 * 
 * Provides functions to manage domain card vault/loadout operations
 * following official Daggerheart rules
 * 
 * Migrated to use enhanced character methods directly instead of updateCharacterData
 */

import { useCallback } from 'react';
import { CharacterData } from '../types/character';
import { 
  analyzeLoadoutState,
  canMoveToLoadout,
  canMoveToVault,
  LoadoutInfo
} from '../utils/vaultLoadoutManager';

// Interface for enhanced character domain card operations
interface DomainCardOperations {
  addDomainCardToLoadout?: (cardId: string, autoVault?: boolean) => void;
  moveDomainCardToVault?: (cardId: string) => void;
  moveDomainCardToLoadout?: (cardId: string) => void;
  removeDomainCard?: (cardId: string) => void;
}

export interface UseVaultLoadoutReturn {
  // State analysis
  analyzeState: () => LoadoutInfo;
  
  // Operations
  addToLoadout: (cardId: string, autoVault?: boolean) => Promise<{ success: boolean; error?: string }>;
  moveToVault: (cardId: string) => Promise<{ success: boolean; error?: string }>;
  moveToLoadout: (cardId: string) => Promise<{ success: boolean; error?: string }>;
  removeCompletely: (cardId: string) => Promise<{ success: boolean; error?: string }>;
  
  // Validation
  canAddToLoadout: (cardId: string) => boolean;
  canMoveToLoadout: (cardId: string) => { valid: boolean; reason?: string };
  canMoveToVault: (cardId: string) => { valid: boolean; reason?: string };
}

export const useVaultLoadout = (
  character: CharacterData | null,
  enhancedCharacter: DomainCardOperations
): UseVaultLoadoutReturn => {
  
  const loadout = character?.inventory?.domainCardLoadout || [];
  const vault = character?.inventory?.domainCardVault || [];
  
  const analyzeState = useCallback((): LoadoutInfo => {
    return analyzeLoadoutState(loadout, vault);
  }, [loadout, vault]);
  
  const addToLoadout = useCallback(async (
    cardId: string, 
    autoVault: boolean = true
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!enhancedCharacter.addDomainCardToLoadout) {
        return { 
          success: false, 
          error: 'addDomainCardToLoadout method not available'
        };
      }
      
      enhancedCharacter.addDomainCardToLoadout(cardId, autoVault);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to add card to loadout'
      };
    }
  }, [enhancedCharacter]);
  
  const moveToVault = useCallback(async (
    cardId: string
  ): Promise<{ success: boolean; error?: string }> => {
    const validation = canMoveToVault(cardId, loadout, vault);
    if (!validation.valid) {
      return { success: false, error: validation.reason };
    }
    
    try {
      if (!enhancedCharacter.moveDomainCardToVault) {
        return { 
          success: false, 
          error: 'moveDomainCardToVault method not available'
        };
      }
      
      enhancedCharacter.moveDomainCardToVault(cardId);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to move card to vault'
      };
    }
  }, [loadout, vault, enhancedCharacter]);
  
  const moveToLoadout = useCallback(async (
    cardId: string
  ): Promise<{ success: boolean; error?: string }> => {
    const validation = canMoveToLoadout(cardId, loadout, vault);
    if (!validation.valid) {
      return { success: false, error: validation.reason };
    }
    
    try {
      if (!enhancedCharacter.moveDomainCardToLoadout) {
        return { 
          success: false, 
          error: 'moveDomainCardToLoadout method not available'
        };
      }
      
      enhancedCharacter.moveDomainCardToLoadout(cardId);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to move card to loadout'
      };
    }
  }, [loadout, vault, enhancedCharacter]);
  
  const removeCompletely = useCallback(async (
    cardId: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!enhancedCharacter.removeDomainCard) {
        return { 
          success: false, 
          error: 'removeDomainCard method not available'
        };
      }
      
      enhancedCharacter.removeDomainCard(cardId);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to remove card'
      };
    }
  }, [enhancedCharacter]);
  
  const canAddToLoadoutCheck = useCallback((cardId: string): boolean => {
    return loadout.length < 5 && !loadout.some(ref => ref.contentId === cardId) && !vault.some(ref => ref.contentId === cardId);
  }, [loadout, vault]);
  
  return {
    analyzeState,
    addToLoadout,
    moveToVault,
    moveToLoadout,
    removeCompletely,
    canAddToLoadout: canAddToLoadoutCheck,
    canMoveToLoadout: (cardId: string) => canMoveToLoadout(cardId, loadout, vault),
    canMoveToVault: (cardId: string) => canMoveToVault(cardId, loadout, vault)
  };
};